import { addToast, Badge, Button, Divider, Input } from '@heroui/react'
import { AlertCircle, Loader, Play } from 'lucide-react'
import { useCallback, useContext, useEffect, useRef, useState } from 'react'

import FlowContext from '../../contextFlow'

import ClusterGenerationLoader from './CustomCodeComponents/ClusterGenerationLoader'
// Components
import CodeEditorSection from './CustomCodeComponents/CodeEditorSection'
import PayloadEditor from './CustomCodeComponents/PayloadEditor'

function CustomCodeModal({ id, data, setIsOpen, nodeType, nodeOptions, onNodeTypeChange }) {
  const { triggerUpdate, elements } = useContext(FlowContext)
  let isWarnToast = false
  const codeEditorRef = useRef(null)

  // State to track if this is a first-time script creation
  const [isGeneratingCluster, setIsGeneratingCluster] = useState(false)

  // Basic node properties
  const [name, setName] = useState(data.name || '')
  const [snippetVariables, setSnippetVariables] = useState(data.snippet_variables || {})
  const [originalVariables, setOriginalVariables] = useState({}) // Store original variables for test mode
  const [testMode, setTestMode] = useState(false) // New state for test mode
  const [code, setCode] = useState(data.code || '')
  const [hasCodeChanged, setHasCodeChanged] = useState(false)

  // Extract variables state
  const [variableValidationErrors, setVariableValidationErrors] = useState([])

  // Code editor state
  const [apiResponse, setApiResponse] = useState(null)
  const [showTestResponse, setShowTestResponse] = useState(false)
  const [isTestingCode, setIsTestingCode] = useState(false)

  // Check if this is a first-time script creation when the component mounts
  useEffect(() => {
    const isFirstTimeCreation = !data.code && !data.snippet_id

    if (isFirstTimeCreation) {
      setIsGeneratingCluster(true)

      // Show the loading animation for 5 seconds
      const timer = setTimeout(() => {
        setIsGeneratingCluster(false)
      }, 5000)

      return () => clearTimeout(timer)
    }
  }, [data])

  // Sync test payload with script variables
  const syncTestPayloadWithVariables = (newVariables, currentTestPayload = {}) => {
    // Create a new test payload that preserves existing values
    const syncedPayload = { ...currentTestPayload }

    // Remove any keys that no longer exist in the script variables
    Object.keys(syncedPayload).forEach((key) => {
      if (!newVariables.hasOwnProperty(key)) {
        delete syncedPayload[key]
      }
    })

    // Add any new keys from script variables, but keep existing values
    Object.entries(newVariables).forEach(([key, value]) => {
      if (!syncedPayload.hasOwnProperty(key)) {
        syncedPayload[key] = value
      }
    })

    return syncedPayload
  }

  // Handle generating variable extraction code
  const handleGenerateVariableCode = (variableNames) => {
    // Check if variableNames is valid
    if (!variableNames || !Array.isArray(variableNames) || variableNames.length === 0) {
      console.warn('Invalid variable names for code generation:', variableNames)
      addToast({
        title: 'Cannot generate code',
        description: 'No variables were selected for code generation.',
        variant: 'warning',
      })
      return
    }

    // Check if editor reference is available
    if (!codeEditorRef.current) {
      console.error('Code editor reference is not available yet')
      addToast({
        title: 'Code editor not initialized',
        description: 'Please wait for the editor to fully load and try again.',
        variant: 'error',
      })
      return
    }

    // Check if any variable name has spaces (for notification purposes only)
    const hasSpaces = variableNames.some(name => name.includes(' '))

    try {
      // Directly access the function from the ref instead of using the state
      const success = codeEditorRef.current.generateVariableExtractionCode(variableNames)

      if (success) {
        // Add a message about space handling if relevant
        const conversionMessage = hasSpaces
          ? ' Variable names with spaces will be declared with underscores in the code.'
          : ''

        addToast({
          title: `Generated code for ${variableNames.length} variable${variableNames.length !== 1 ? 's' : ''}`,
          description: `This code will be used to extract variables from the response.${conversionMessage}`,
          variant: 'success',
        })
      }
      else {
        addToast({
          title: 'Failed to generate code',
          description: 'Could not find a suitable place in your code to insert variable extraction.',
          variant: 'error',
        })
      }
    }
    catch (error) {
      console.error('Error generating variable code:', error)
      addToast({
        title: 'Failed to generate code',
        description: error?.message || 'An unexpected error occurred',
        variant: 'error',
      })
    }
  }

  // Handle code changes from CodeEditorSection
  const handleCodeChange = (newCode) => {
    setCode(newCode)
    setHasCodeChanged(true)
  }

  // Handle snippet ID changes, immediately updating the node
  const handleSnippetIdChange = (newSnippetId) => {
    if (newSnippetId && newSnippetId !== data.snippet_id) {
      console.log('newSnippetId', newSnippetId)
      // Update data object
      data.snippet_id = newSnippetId

      // Immediately update the node in the flow
      const updatedNodes = elements.nodes.map((element_node) => {
        if (element_node.id === id) {
          const node = structuredClone(element_node)
          node.data.snippet_id = newSnippetId
          return node
        }
        return element_node
      })

      // Trigger flow update with the new snippet ID
      triggerUpdate(
        {
          nodes: updatedNodes,
          edges: elements.edges,
        },
        false,
      )

      console.log('saved node with snippet id', newSnippetId)
    }
  }

  // Handle complete script creation event
  const handleScriptCreated = (scriptData) => {
    if (scriptData) {
      if (scriptData.snippet_id) {
        handleSnippetIdChange(scriptData.snippet_id)
      }
      if (scriptData.code) {
        setCode(scriptData.code)
      }
    }
  }

  // Update snippetVariables when they change in the PayloadEditor
  const handleSnippetVariablesChange = (newVariables) => {
    // Set the new variables immediately
    setSnippetVariables(newVariables)
  }

  // Add a default variable when none exist
  const handleAddFirstVariable = () => {
    setSnippetVariables((prevVars) => {
      // Skip if we already have variables
      if (Object.keys(prevVars).length > 0) {
        return prevVars
      }
      return { callVariable: '' }
    })
  }

  // Functions for validation
  const validateVariables = () => {
    if (!data.extractVars) {
      return true
    }

    const extractVars = data.extractVars || [['', '', '', false]]

    const errors = []
    extractVars.forEach((variable, index) => {
      variable.forEach((field, fieldIndex) => {
        if (typeof field === 'string' && field.trim() === '') {
          errors.push({
            index,
            field: fieldIndex,
            message: 'Field is required',
          })
        }
      })
    })
    setVariableValidationErrors(errors)
    return errors.length === 0
  }

  // Effects for handling data changes
  useEffect(() => {
    setName(data.name || '')
    setSnippetVariables(data.snippet_variables || {})
    setCode(data.code || '')
  }, [data])

  const handleSave = async () => {
    if (validateVariables()) {
      // Make sure we're using the real variables, not test ones
      if (testMode) {
        toggleTestMode()
      }

      // If code has changed, run the update mutation from the code editor first
      if (hasCodeChanged && codeEditorRef.current && codeEditorRef.current.handleSave) {
        try {
          await codeEditorRef.current.handleSave()
        }
        catch (error) {
          console.error('Error saving code:', error)
          addToast({
            title: 'Failed to save code changes',
            description: error?.message || 'An unexpected error occurred',
            variant: 'error',
          })
          return // Don't proceed if code save failed
        }
      }

      let updatedEdges = elements.edges
      const updatedNodes = elements.nodes.map((element_node) => {
        if (element_node.id === id) {
          const node = structuredClone(element_node)
          node.data.name = name
          node.type = 'Custom Code'

          // Add snippet variables to node data
          if (Object.keys(snippetVariables).length > 0) {
            node.data.snippet_variables = snippetVariables
          }
          else {
            delete node.data.snippet_variables
          }

          if (data.snippet_id) {
            node.data.snippet_id = data.snippet_id
          }

          // Update the code if it has changed
          if (hasCodeChanged) {
            node.data.code = code
          }

          console.log('updated node', node)

          return node
        }
        return element_node
      })

      if (!isWarnToast) {
        triggerUpdate(
          {
            nodes: updatedNodes,
            edges: updatedEdges,
          },
          false,
        )

        setIsOpen(false)
      }
      isWarnToast = false
    }
  }

  // Toggle test mode functionality
  const toggleTestMode = () => {
    if (!testMode) {
      // Entering test mode - save original variables
      setOriginalVariables(snippetVariables)

      // Create a test payload with the same keys but empty values
      const emptyTestPayload = {}
      Object.keys(snippetVariables).forEach((key) => {
        emptyTestPayload[key] = ''
      })

      setSnippetVariables(emptyTestPayload)
      setApiResponse(null)
      setShowTestResponse(false)
    }
    else {
      // Exiting test mode - restore original variables
      setSnippetVariables(originalVariables)
    }

    setTestMode(!testMode)
  }

  // Handle running the test
  const handleRunTest = async () => {
    setIsTestingCode(true)
    try {
      // Use the CodeEditorSection's test method via ref
      const response = await codeEditorRef.current.executeTest()
      setApiResponse(response)
      setShowTestResponse(true)
    }
    catch (error) {
      console.error('Test error:', error)
      addToast({
        title: 'Test failed',
        description: error.message || 'An error occurred during testing',
        variant: 'error',
      })
    }
    finally {
      setIsTestingCode(false)
    }
  }

  return (
    <div className="space-y-6 w-full relative">
      {/* Show loading animation when generating a new cluster */}
      {isGeneratingCluster ? (
        <ClusterGenerationLoader />
      ) : (
        <>
          {/* Node name and type - now in a flex layout */}
          <div className="flex items-start gap-4">
            <div className="space-y-1.5 flex-1">
              <label className="text-sm font-medium">Node Name:</label>
              <Input
                value={name}
                radius="none"
                onChange={e => setName(e.target.value)}
                variant="bordered"
                className="w-full"
                placeholder="Enter node name"
              />
            </div>

            {/* <div className="space-y-1.5 min-w-[220px]">
              <label className="text-sm font-medium">Node Type:</label>
              <Select
                value={nodeType}
                onValueChange={onNodeTypeChange}
                radius='none'
                variant="bordered"
                className="w-full"
                placeholder="Select node type"
              >
                {nodeOptions.map(option => (
                  <SelectItem key={option.id} value={option.value}>
                    {option.name}
                  </SelectItem>
                ))}
              </Select>
            </div> */}
          </div>

          <Divider />

          {/* Variables Section - Removed yellow background */}
          <div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
              </div>
            </div>
            <PayloadEditor
              value={snippetVariables}
              onChange={handleSnippetVariablesChange}
              className="w-full"
              testMode={testMode}
              onGenerateCode={handleGenerateVariableCode}
              onAddFirstVariable={handleAddFirstVariable}
              title={testMode ? 'Mock Test Variables' : 'Reference Variables'}
              description={
                testMode
                  ? 'Configure mock values for testing your code. Real variables will be restored after testing.'
                  : (
                      <>
                        These variables can be referenced in your code using
                        <code className="bg-neutral-100 px-1 py-0.5 rounded text-xs">json['variable_name']</code>
                      </>
                    )
              }
            />
          </div>

          <Divider />

          {/* Code Editor Section */}
          <div>
            <div className="flex justify-between items-center">
              <div>
                <h3 className="text-base font-medium mb-1">Custom Code Execution</h3>
                <p className="text-sm text-gray-600">
                  The JSON object you pass in the response will be stored as variables that future nodes can reference.
                </p>
              </div>

              <div className="flex items-center space-x-2">
                <span className="text-sm font-medium">Test Mode</span>
                <Button
                  size="sm"
                  radius="full"
                  variant={testMode ? 'solid' : 'bordered'}
                  color={testMode ? 'default' : 'default'}
                  className={`min-w-[60px] h-6 transition-all ${testMode ? 'bg-black text-white' : 'bg-transparent text-black border-gray-300'}`}
                  onPress={toggleTestMode}
                >
                  {testMode ? 'ON' : 'OFF'}
                </Button>
              </div>
            </div>

            {/* Code editor with improved floating test button */}
            <div className="relative">
              <CodeEditorSection
                ref={codeEditorRef}
                data={{
                  ...data,
                  code,
                }}
                onCodeChange={handleCodeChange}
                onSnippetIdChange={handleSnippetIdChange}
                onScriptCreated={handleScriptCreated}
                isExpanded
                setIsExpanded={() => {}}
                setApiResponse={setApiResponse}
                snippetVariables={snippetVariables}
                hideButtons // Hide the buttons in the CodeEditorSection
              />

              {/* Improved floating Run Test button */}
              {testMode && (
                <div className="absolute top-4 right-4 z-10">
                  <Button
                    variant="solid"
                    color="default"
                    radius="none"
                    size="sm"
                    onPress={handleRunTest}
                    isDisabled={isTestingCode}
                    className="bg-white shadow-md hover:shadow-lg transition-all px-3 flex items-center gap-1 border border-slate-200 hover:bg-slate-50"
                    startContent={
                      isTestingCode
                        ? <Loader className="h-4 w-4 animate-spin" />
                        : <Play className="h-4 w-4 fill-black" />
                    }
                  >
                    {isTestingCode ? 'Running...' : 'Run Test'}
                  </Button>
                </div>
              )}
            </div>
          </div>

          {/* Response Output Section - Improved UI */}
          {testMode && showTestResponse && apiResponse && (
            <div className="mt-6 border border-slate-200 rounded-md overflow-hidden">
              {apiResponse.success === false ? (
                // Error Display
                <div>
                  <div className="border-b border-slate-200 bg-white px-4 py-2 flex items-center gap-2">
                    <h3 className="text-base font-medium text-gray-800">Response Error</h3>
                    <Badge color="danger" variant="flat" size="sm" className="bg-red-50 text-red-700 border-red-100">Failed</Badge>
                  </div>
                  <div className="p-6 bg-white">
                    <div className="border-l-4 border-red-400 p-4 rounded bg-white shadow-sm">
                      <div className="flex items-start">
                        <div className="flex-shrink-0">
                          <AlertCircle className="h-5 w-5 text-red-500" />
                        </div>
                        <div className="ml-3">
                          <h3 className="text-sm font-medium text-gray-800">
                            {apiResponse.response?.error?.code || 'Error'}
                          </h3>
                          <div className="mt-2 text-sm text-gray-700">
                            <p>{apiResponse.response?.error?.message || 'An unknown error occurred'}</p>
                          </div>
                          {apiResponse.response?.status_code && (
                            <div className="mt-3">
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 border border-red-200">
                                Status:
                                {' '}
                                {apiResponse.response.status_code}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                // Success Display
                <div>
                  <div className="border-b border-slate-200 bg-white px-4 py-2 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <h3 className="text-base font-medium text-gray-800">Response Success</h3>
                      <Badge color="success" variant="flat" size="sm" className="bg-green-50 text-green-700 border-green-100">
                        Status:
                        {' '}
                        {apiResponse.response?.status_code || 200}
                      </Badge>
                    </div>
                    {apiResponse.response?.exec_time && (
                      <div className="flex items-center gap-1">
                        <span className="text-xs text-gray-500">Latency:</span>
                        <Badge variant="flat" color="default" size="sm" className="font-mono bg-gray-100 text-gray-700">
                          {apiResponse.response.exec_time.toFixed(2)}
                          ms
                        </Badge>
                      </div>
                    )}
                  </div>
                  <div className="p-4 bg-white">
                    {apiResponse.response?.content
                      ? (
                          <div className="divide-y divide-slate-100">
                            {Object.entries(apiResponse.response.content).map(([key, value]) => (
                              <div key={key} className="py-2 first:pt-0 last:pb-0">
                                <div className="flex flex-col gap-1">
                                  <div className="text-sm font-medium text-gray-700">{key}</div>
                                  <div className="text-sm text-gray-600 bg-gray-50 p-2 rounded border border-gray-100">
                                    {typeof value === 'object'
                                      ? JSON.stringify(value, null, 2)
                                      : String(value)}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )
                      : (
                          <div className="text-center text-gray-500 py-4">
                            No content returned in the response
                          </div>
                        )}
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Sticky Footer with Action Buttons */}
      <div className="sticky -bottom-5 left-0 right-0 bg-white border-t border-gray-200 p-4 z-50 flex justify-between items-center">
        <div className="flex w-full justify-end">
          <div className="flex space-x-3">
            <Button
              onPress={() => setIsOpen(false)}
              radius="none"
            >
              Cancel
            </Button>
            <Button
              onPress={handleSave}
              radius="none"
              className="bg-black hover:bg-gray-800 text-white px-4 py-2"
            >
              Save
            </Button>
          </div>
        </div>

        <div className="flex items-center">
          {variableValidationErrors.length > 0 && (
            <div className="relative group mr-2">
              <AlertCircle className="h-5 w-5 text-red-500" />
              <span className="absolute left-full ml-2 top-1/2 transform -translate-y-1/2 bg-red-100 text-red-800 text-xs font-medium px-2 py-1 rounded-sm hidden group-hover:block whitespace-nowrap z-50">
                Some variables are missing fields
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default CustomCodeModal
